import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";

export default function PhotoAttachments({ entityType, entityId, label = "Site Photos" }) {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => {
    if (!entityId) return;
    api.get("/photos", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setPhotos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [entityId]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("entity_type", entityType);
        fd.append("entity_id", entityId);
        await api.post("/photos", fd);
      }
      toast.success(files.length > 1 ? `${files.length} photos added` : "Photo added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      setPhotos(photos.filter((p) => p.photo_id !== id));
    } catch {
      toast.error("Could not remove photo. Please try again.");
      load();
    }
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">{label} ({photos.length})</span>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload}
          data-testid={`photo-input-${entityType}-${entityId}`} />
        <Button size="sm" variant="outline" className="rounded-full gap-1.5 h-8" disabled={busy}
          onClick={() => fileRef.current.click()} data-testid={`photo-add-${entityType}-${entityId}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Add Photos
        </Button>
      </div>
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <div key={p.photo_id} className="relative group" data-testid={`photo-thumb-${p.photo_id}`}>
              <a href={`${API}/photos/${p.photo_id}/file`} target="_blank" rel="noreferrer">
                <img src={`${API}/photos/${p.photo_id}/file?thumb=true`} alt={p.original_filename}
                  className="w-20 h-20 object-cover rounded-lg border" loading="lazy" />
              </a>
              <button onClick={() => remove(p.photo_id)} data-testid={`photo-del-${p.photo_id}`}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
